import type { ControlState } from '../game/types';
import { t } from '../i18n';

export type TouchControlMode = 'dpad' | 'stick';

interface TouchControlsOptions {
  root: HTMLElement;
  onChange: (controls: ControlState) => void;
  onModeChange?: (mode: TouchControlMode) => void;
  onPause: () => void;
}

const EMPTY_CONTROL: ControlState = {
  up: false,
  down: false,
  left: false,
  right: false,
  fire: false,
};

const STORAGE_KEY = 'siege-tank-touch-mode';
const LAYOUT_STORAGE_KEY = 'siege-tank-touch-layout';
const PANEL_MARGIN = 8;

interface StoredPanelPosition {
  x: number;
  y: number;
}

interface StoredLayout {
  left?: StoredPanelPosition;
  right?: StoredPanelPosition;
}

interface PanelDrag {
  panel: HTMLElement;
  pointerId: number;
  offsetX: number;
  offsetY: number;
}

export class TouchControlsController {
  private controls: ControlState = { ...EMPTY_CONTROL };
  private mode: TouchControlMode = 'dpad';
  private enabled = false;
  private pauseOnly = false;
  private layoutUnlocked = false;
  private panelDrag: PanelDrag | null = null;
  private stickPointerId: number | null = null;
  private dpadPointers = new Map<number, keyof ControlState>();
  private firePointers = new Set<number>();
  private leftPanel: HTMLElement;
  private rightPanel: HTMLElement;
  private layoutLockButton: HTMLElement;
  private stickBase: HTMLElement;
  private stickKnob: HTMLElement;
  private modeButtons: HTMLButtonElement[];

  constructor(private options: TouchControlsOptions) {
    this.leftPanel = required(options.root, 'touch-left');
    this.rightPanel = required(options.root, 'touch-right');
    this.layoutLockButton = required(options.root, 'touch-layout-lock');
    this.stickBase = required(options.root, 'touch-stick');
    this.stickKnob = required(options.root, 'touch-stick-knob');
    this.modeButtons = Array.from(options.root.querySelectorAll<HTMLButtonElement>('[data-touch-mode]'));
    this.mode = readStoredMode() ?? 'dpad';
    this.applyMode(this.mode);
    this.setEnabled(false);
    this.applyStoredLayout();
    this.bindModeControls();
    this.bindLayoutControls();
    this.bindDpadControls();
    this.bindStickControls();
    this.bindFireControl();
    this.bindPauseControl();
    window.addEventListener('resize', () => this.clampStoredPanels());
    this.refreshLocale();
    this.emit();
  }

  setMode(mode: TouchControlMode): void {
    if (this.mode === mode) {
      this.applyMode(mode);
      return;
    }
    this.applyMode(mode);
    this.resetMovement();
    writeStoredMode(mode);
  }

  setEnabled(enabled: boolean, pauseOnly = false): void {
    if (this.enabled === enabled && this.pauseOnly === pauseOnly && this.options.root.hasAttribute('aria-hidden')) return;
    this.enabled = enabled;
    this.pauseOnly = pauseOnly && !enabled;
    const visible = enabled || this.pauseOnly;
    this.options.root.classList.toggle('enabled', visible);
    this.options.root.classList.toggle('pause-only', this.pauseOnly);
    this.options.root.setAttribute('aria-hidden', String(!visible));
    if (!enabled) {
      if (this.layoutUnlocked) this.setLayoutUnlocked(false);
      this.resetMovement();
    }
  }

  getMode(): TouchControlMode {
    return this.mode;
  }

  refreshLocale(): void {
    this.layoutLockButton.textContent = this.layoutUnlocked ? t('touch.lockLayout') : t('touch.unlockLayout');
  }

  private bindModeControls(): void {
    for (const button of this.modeButtons) {
      button.addEventListener('click', () => {
        const mode = normalizeMode(button.dataset.touchMode);
        this.setMode(mode);
        this.options.onModeChange?.(mode);
      });
    }
  }

  private bindDpadControls(): void {
    for (const button of this.options.root.querySelectorAll<HTMLButtonElement>('[data-touch-dir]')) {
      const direction = normalizeDirection(button.dataset.touchDir);
      if (!direction) continue;
      button.addEventListener('pointerdown', (event) => {
        event.preventDefault();
        if (this.layoutUnlocked) return;
        capturePointer(button, event.pointerId);
        this.dpadPointers.set(event.pointerId, direction);
        this.controls[direction] = true;
        button.classList.add('active');
        this.emit();
      });
      const release = (event: PointerEvent) => {
        if (this.dpadPointers.get(event.pointerId) !== direction) return;
        this.dpadPointers.delete(event.pointerId);
        this.controls[direction] = false;
        button.classList.remove('active');
        this.emit();
      };
      button.addEventListener('pointerup', release);
      button.addEventListener('pointercancel', release);
      button.addEventListener('lostpointercapture', release);
      button.addEventListener('contextmenu', (event) => event.preventDefault());
    }
  }

  private bindStickControls(): void {
    this.stickBase.addEventListener('pointerdown', (event) => {
      event.preventDefault();
      if (this.layoutUnlocked) return;
      this.stickPointerId = event.pointerId;
      capturePointer(this.stickBase, event.pointerId);
      this.stickBase.classList.add('active');
      this.updateStick(event);
    });
    this.stickBase.addEventListener('pointermove', (event) => {
      if (event.pointerId !== this.stickPointerId) return;
      event.preventDefault();
      this.updateStick(event);
    });
    const releaseStick = (event: PointerEvent) => {
      if (event.pointerId !== this.stickPointerId) return;
      this.stickPointerId = null;
      this.stickBase.classList.remove('active');
      this.stickKnob.style.transform = '';
      this.clearDirections();
      this.emit();
    };
    this.stickBase.addEventListener('pointerup', releaseStick);
    this.stickBase.addEventListener('pointercancel', releaseStick);
    this.stickBase.addEventListener('lostpointercapture', releaseStick);
    this.stickBase.addEventListener('contextmenu', (event) => event.preventDefault());
  }

  private bindFireControl(): void {
    const fireButton = required(this.options.root, 'touch-fire');
    fireButton.addEventListener('pointerdown', (event) => {
      event.preventDefault();
      if (this.layoutUnlocked) return;
      capturePointer(fireButton, event.pointerId);
      this.firePointers.add(event.pointerId);
      this.controls.fire = true;
      fireButton.classList.add('active');
      this.emit();
    });
    const release = (event: PointerEvent) => {
      if (!this.firePointers.has(event.pointerId)) return;
      this.firePointers.delete(event.pointerId);
      this.controls.fire = this.firePointers.size > 0;
      if (!this.controls.fire) fireButton.classList.remove('active');
      this.emit();
    };
    fireButton.addEventListener('pointerup', release);
    fireButton.addEventListener('pointercancel', release);
    fireButton.addEventListener('lostpointercapture', release);
    fireButton.addEventListener('contextmenu', (event) => event.preventDefault());
  }

  private bindPauseControl(): void {
    const pauseButton = required(this.options.root, 'touch-pause');
    pauseButton.addEventListener('click', (event) => {
      event.preventDefault();
      if (this.layoutUnlocked) return;
      this.options.onPause();
    });
  }

  private bindLayoutControls(): void {
    this.layoutLockButton.addEventListener('click', (event) => {
      event.preventDefault();
      this.setLayoutUnlocked(!this.layoutUnlocked);
    });
    this.bindPanelDrag(this.leftPanel);
    this.bindPanelDrag(this.rightPanel);
  }

  private bindPanelDrag(panel: HTMLElement): void {
    panel.addEventListener('pointerdown', (event) => {
      if (!this.layoutUnlocked || isLayoutControl(event.target)) return;
      event.preventDefault();
      this.resetMovement();
      const rect = panel.getBoundingClientRect();
      this.panelDrag = {
        panel,
        pointerId: event.pointerId,
        offsetX: event.clientX - rect.left,
        offsetY: event.clientY - rect.top,
      };
      panel.classList.add('dragging');
      capturePointer(panel, event.pointerId);
      this.movePanelDrag(event);
    });
    panel.addEventListener('pointermove', (event) => {
      if (this.panelDrag?.pointerId !== event.pointerId) return;
      event.preventDefault();
      this.movePanelDrag(event);
    });
    const release = (event: PointerEvent) => {
      if (this.panelDrag?.pointerId !== event.pointerId) return;
      this.panelDrag.panel.classList.remove('dragging');
      this.panelDrag = null;
      this.saveLayout();
    };
    panel.addEventListener('pointerup', release);
    panel.addEventListener('pointercancel', release);
    panel.addEventListener('lostpointercapture', release);
  }

  private movePanelDrag(event: PointerEvent): void {
    if (!this.panelDrag) return;
    const rect = this.panelDrag.panel.getBoundingClientRect();
    const nextX = clamp(event.clientX - this.panelDrag.offsetX, PANEL_MARGIN, window.innerWidth - rect.width - PANEL_MARGIN);
    const nextY = clamp(event.clientY - this.panelDrag.offsetY, PANEL_MARGIN, window.innerHeight - rect.height - PANEL_MARGIN);
    this.applyPanelPixels(this.panelDrag.panel, nextX, nextY);
  }

  private setLayoutUnlocked(unlocked: boolean): void {
    if (this.layoutUnlocked === unlocked) return;
    this.layoutUnlocked = unlocked;
    this.options.root.classList.toggle('layout-unlocked', unlocked);
    this.options.root.dataset.layout = unlocked ? 'unlocked' : 'locked';
    this.refreshLocale();
    this.layoutLockButton.setAttribute('aria-pressed', String(unlocked));
    this.resetMovement();
    if (!unlocked) this.saveLayout();
  }

  private updateStick(event: PointerEvent): void {
    const rect = this.stickBase.getBoundingClientRect();
    const radius = Math.min(rect.width, rect.height) * 0.34;
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    const rawX = event.clientX - centerX;
    const rawY = event.clientY - centerY;
    const distance = Math.hypot(rawX, rawY);
    const scale = distance > radius ? radius / distance : 1;
    const dx = rawX * scale;
    const dy = rawY * scale;
    this.stickKnob.style.transform = `translate(${dx}px, ${dy}px)`;
    this.clearDirections();
    if (distance >= radius * 0.34) {
      if (Math.abs(rawX) > Math.abs(rawY)) this.controls[rawX < 0 ? 'left' : 'right'] = true;
      else this.controls[rawY < 0 ? 'up' : 'down'] = true;
    }
    this.emit();
  }

  private applyMode(mode: TouchControlMode): void {
    this.mode = mode;
    this.options.root.dataset.touchMode = mode;
    for (const button of this.modeButtons) {
      const active = normalizeMode(button.dataset.touchMode) === mode;
      button.classList.toggle('active', active);
      button.setAttribute('aria-pressed', String(active));
    }
  }

  private resetMovement(): void {
    this.stickPointerId = null;
    this.dpadPointers.clear();
    this.stickBase.classList.remove('active');
    this.stickKnob.style.transform = '';
    this.clearDirections();
    for (const button of this.options.root.querySelectorAll('[data-touch-dir]')) button.classList.remove('active');
    this.emit();
  }

  private applyStoredLayout(): void {
    const layout = readStoredLayout();
    if (layout.left) this.applyPanelRatio(this.leftPanel, layout.left);
    if (layout.right) this.applyPanelRatio(this.rightPanel, layout.right);
    window.requestAnimationFrame(() => this.clampStoredPanels());
  }

  private applyPanelRatio(panel: HTMLElement, position: StoredPanelPosition): void {
    const rect = panel.getBoundingClientRect();
    const x = clamp(position.x * window.innerWidth, PANEL_MARGIN, window.innerWidth - rect.width - PANEL_MARGIN);
    const y = clamp(position.y * window.innerHeight, PANEL_MARGIN, window.innerHeight - rect.height - PANEL_MARGIN);
    this.applyPanelPixels(panel, x, y);
  }

  private applyPanelPixels(panel: HTMLElement, x: number, y: number): void {
    panel.style.left = `${Math.round(x)}px`;
    panel.style.top = `${Math.round(y)}px`;
    panel.style.right = 'auto';
    panel.style.bottom = 'auto';
  }

  private clampStoredPanels(): void {
    this.clampPanel(this.leftPanel);
    this.clampPanel(this.rightPanel);
  }

  private clampPanel(panel: HTMLElement): void {
    if (!panel.style.left || !panel.style.top) return;
    const rect = panel.getBoundingClientRect();
    const nextX = clamp(rect.left, PANEL_MARGIN, window.innerWidth - rect.width - PANEL_MARGIN);
    const nextY = clamp(rect.top, PANEL_MARGIN, window.innerHeight - rect.height - PANEL_MARGIN);
    this.applyPanelPixels(panel, nextX, nextY);
  }

  private saveLayout(): void {
    const layout: StoredLayout = {};
    if (this.leftPanel.style.left && this.leftPanel.style.top) layout.left = ratioForPanel(this.leftPanel);
    if (this.rightPanel.style.left && this.rightPanel.style.top) layout.right = ratioForPanel(this.rightPanel);
    writeStoredLayout(layout);
  }

  private clearDirections(): void {
    this.controls.up = false;
    this.controls.down = false;
    this.controls.left = false;
    this.controls.right = false;
  }

  private emit(): void {
    this.options.onChange({ ...this.controls });
  }
}

function normalizeMode(value: string | undefined): TouchControlMode {
  return value === 'stick' ? 'stick' : 'dpad';
}

function normalizeDirection(value: string | undefined): keyof ControlState | null {
  if (value === 'up' || value === 'down' || value === 'left' || value === 'right') return value;
  return null;
}

function readStoredMode(): TouchControlMode | null {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    return value === 'stick' || value === 'dpad' ? value : null;
  } catch {
    return null;
  }
}

function writeStoredMode(mode: TouchControlMode): void {
  try {
    localStorage.setItem(STORAGE_KEY, mode);
  } catch {
    // Ignore private browsing storage failures.
  }
}

function readStoredLayout(): StoredLayout {
  try {
    const raw = localStorage.getItem(LAYOUT_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as StoredLayout;
    return {
      left: normalizeStoredPosition(parsed.left),
      right: normalizeStoredPosition(parsed.right),
    };
  } catch {
    return {};
  }
}

function writeStoredLayout(layout: StoredLayout): void {
  try {
    localStorage.setItem(LAYOUT_STORAGE_KEY, JSON.stringify(layout));
  } catch {
    // Ignore private browsing storage failures.
  }
}

function normalizeStoredPosition(position: StoredPanelPosition | undefined): StoredPanelPosition | undefined {
  if (!position || !Number.isFinite(position.x) || !Number.isFinite(position.y)) return undefined;
  return {
    x: clamp(position.x, 0, 1),
    y: clamp(position.y, 0, 1),
  };
}

function ratioForPanel(panel: HTMLElement): StoredPanelPosition {
  const rect = panel.getBoundingClientRect();
  return {
    x: clamp(rect.left / Math.max(1, window.innerWidth), 0, 1),
    y: clamp(rect.top / Math.max(1, window.innerHeight), 0, 1),
  };
}

function isLayoutControl(target: EventTarget | null): boolean {
  return target instanceof Element && Boolean(target.closest('#touch-layout-lock, .touch-mode-tabs button'));
}

function clamp(value: number, min: number, max: number): number {
  if (max < min) return min;
  return Math.max(min, Math.min(max, value));
}

function required(root: HTMLElement, id: string): HTMLElement {
  const element = root.querySelector<HTMLElement>(`#${id}`);
  if (!element) throw new Error(`Missing touch control #${id}`);
  return element;
}

function capturePointer(element: HTMLElement, pointerId: number): void {
  try {
    element.setPointerCapture(pointerId);
  } catch {
    // Synthetic pointer events and a few embedded browsers can reject capture.
  }
}
